import { AuthorizationReview } from '@/components/oauth/authorization-review';
import { ConsentDecisionActions, ConsentFooter } from '@/components/oauth/consent-footer';
import { ConsentScreen } from '@/components/oauth/consent-screen';
import { ResourceScopeNote } from '@/components/oauth/resource-scope-note';
import { WorkspaceAccessPicker } from '@/components/oauth/workspace-access-picker';
import { FullPageSpinner } from '@/components/ui/full-page-spinner';
import { useConsentGrant } from '@/hooks/use-consent-grant';
import { useLoginRedirect } from '@/hooks/use-login-redirect';
import { API_URL } from '@/lib/api/core';
import { useAuthState } from '@/lib/auth-state';
import { oauthErrorMessage, redirectToOAuthLogin } from '@/lib/oauth-consent';
import { pruneSearch } from '@/lib/router-search';
import { validateOAuthRedirectUrl } from '@/lib/url';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

type ConsentSearchParams = {
  client_id?: string;
  redirect_uri?: string;
  scope?: string;
  state?: string;
  code_challenge?: string;
  code_challenge_method?: string;
  client_name?: string;
  resource?: string;
  logo_uri?: string;
  client_uri?: string;
  workspace_grants?: '1';
  authorization_intent?: string;
};

type ConsentApiResponse = {
  redirect_url?: string;
  error?: string;
  error_description?: string;
  message?: string;
};

const REQUIRED_PARAMS: Array<keyof ConsentSearchParams> = [
  'client_id',
  'redirect_uri',
  'scope',
  'code_challenge',
  'code_challenge_method',
];

function stringParam(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

export const Route = createFileRoute('/oauth/consent')({
  component: OAuthConsentPage,
  validateSearch: (search: Record<string, unknown>): ConsentSearchParams =>
    pruneSearch({
      client_id: stringParam(search.client_id),
      redirect_uri: stringParam(search.redirect_uri),
      scope: stringParam(search.scope),
      state: stringParam(search.state),
      code_challenge: stringParam(search.code_challenge),
      code_challenge_method: stringParam(search.code_challenge_method),
      client_name: stringParam(search.client_name),
      resource: stringParam(search.resource),
      logo_uri: stringParam(search.logo_uri),
      client_uri: stringParam(search.client_uri),
      authorization_intent: stringParam(search.authorization_intent),
      // `defaultParseSearch` JSON-parses primitive query values, so a real
      // `?workspace_grants=1` navigation arrives here as the number `1`.
      workspace_grants:
        search.workspace_grants === '1' || search.workspace_grants === 1 ? '1' : undefined,
    }),
});

function OAuthConsentPage() {
  const search = Route.useSearch();
  const multiWorkspaceGrantsEnabled = search.workspace_grants === '1';
  const { session, isReady: authReady } = useAuthState();
  const sessionLoading = !authReady;
  const [submitState, setSubmitState] = useState<'approve' | 'deny' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useLoginRedirect(!!session, sessionLoading);

  const missingParams = useMemo(
    () => REQUIRED_PARAMS.filter((key) => !search[key] || search[key]?.length === 0),
    [search],
  );
  const canSubmit = missingParams.length === 0;
  const requesterProvidedName = search.client_name?.trim() || null;
  const scopes = useMemo(() => {
    return Array.from(
      new Set(
        (search.scope || '')
          .split(' ')
          .map((scope) => scope.trim())
          .filter(Boolean),
      ),
    );
  }, [search.scope]);

  const {
    accessLevel,
    setAccessLevel,
    selectedScopes,
    resourceOrg,
    hasMcpScopes,
    requiresWorkspaceAccess,
    orgList,
    workspaceAccess,
    setWorkspaceAccess,
    selectedOrgIds,
    toggleSelectedOrgId,
    orgsLoading,
    orgsError,
    retryOrgs,
    workspaceSelectionReady,
  } = useConsentGrant({
    requestedScopes: scopes,
    resource: search.resource,
    allowMultiple: multiWorkspaceGrantsEnabled,
  });

  const selectedOrgNames = useMemo(
    () => orgList.filter((org) => selectedOrgIds.includes(org.id)).map((org) => org.name),
    [orgList, selectedOrgIds],
  );
  const grantSummary = !requiresWorkspaceAccess
    ? null
    : selectedOrgNames.length === 0
      ? 'Nothing will be granted access.'
      : `${selectedOrgNames.length <= 2 ? selectedOrgNames.join(', ') : `${selectedOrgNames.length} workspaces`} will be granted access.`;
  const approveLabel =
    requiresWorkspaceAccess && multiWorkspaceGrantsEnabled && selectedOrgIds.length > 0
      ? `Approve ${selectedOrgIds.length} ${selectedOrgIds.length === 1 ? 'workspace' : 'workspaces'}`
      : 'Approve';

  const submitConsent = async (approved: boolean) => {
    if (!canSubmit) {
      setError(`Missing required query params: ${missingParams.join(', ')}`);
      return;
    }
    if (approved && requiresWorkspaceAccess && !workspaceSelectionReady) {
      setError(
        orgsError
          ? 'Workspaces could not be loaded. Retry before approving.'
          : 'Choose at least one workspace before approving.',
      );
      return;
    }

    setSubmitState(approved ? 'approve' : 'deny');
    setError(null);

    try {
      const response = await fetch(`${API_URL}/oauth/authorize/consent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          client_id: search.client_id,
          redirect_uri: search.redirect_uri,
          scope: approved ? selectedScopes.join(' ') : search.scope,
          state: search.state ?? '',
          code_challenge: search.code_challenge,
          code_challenge_method: search.code_challenge_method,
          client_name: search.client_name,
          resource: search.resource,
          authorization_intent: search.authorization_intent,
          approved,
          ...(approved && requiresWorkspaceAccess
            ? {
                workspace_access: workspaceAccess,
                organization_ids: selectedOrgIds,
              }
            : {}),
        }),
      });

      const data = (await response.json().catch(() => null)) as ConsentApiResponse | null;

      if (response.status === 401) {
        redirectToOAuthLogin();
        return;
      }

      if (!response.ok) {
        throw new Error(
          oauthErrorMessage(data, `Failed to ${approved ? 'approve' : 'deny'} OAuth consent`),
        );
      }

      const safeRedirect = validateOAuthRedirectUrl(data?.redirect_url);
      if (!safeRedirect) {
        throw new Error('Consent response did not include a valid redirect URL');
      }

      window.location.href = safeRedirect;
    } catch (err) {
      setSubmitState(null);
      setError(err instanceof Error ? err.message : 'Unexpected error while submitting consent');
    }
  };

  if (sessionLoading || !session) {
    return <FullPageSpinner className="min-h-[calc(100vh-3.5rem)]" />;
  }

  return (
    <ConsentScreen
      title="Authorize application"
      busy={submitState !== null}
      error={error}
      review={
        <AuthorizationReview
          idPrefix="oauth-consent"
          clientName={requesterProvidedName}
          requestedScopes={scopes}
          accessLevel={accessLevel}
          onAccessLevelChange={setAccessLevel}
          disabled={submitState !== null}
          resource={search.resource}
          logoUri={search.logo_uri}
          clientUri={search.client_uri}
        />
      }
      aside={
        requiresWorkspaceAccess ? (
          <WorkspaceAccessPicker
            idPrefix="oauth-consent"
            allowMultiple={multiWorkspaceGrantsEnabled}
            orgList={orgList}
            selectedOrgIds={selectedOrgIds}
            onWorkspaceAccessChange={setWorkspaceAccess}
            onSelectedOrgChange={toggleSelectedOrgId}
            orgsLoading={orgsLoading}
            orgsError={orgsError}
            onRetry={retryOrgs}
            disabled={submitState !== null || !canSubmit}
          />
        ) : undefined
      }
      below={
        hasMcpScopes && resourceOrg ? (
          <ResourceScopeNote resourceOrg={resourceOrg} accessLevel={accessLevel} />
        ) : null
      }
      notice={
        !canSubmit ? (
          <p role="alert" className="text-sm text-destructive">
            Missing required query params: {missingParams.join(', ')}
          </p>
        ) : null
      }
      footer={
        <ConsentFooter summary={grantSummary}>
          <ConsentDecisionActions
            busy={submitState}
            approveLabel={approveLabel}
            approveDisabled={
              submitState !== null ||
              !canSubmit ||
              selectedScopes.length === 0 ||
              (requiresWorkspaceAccess && !workspaceSelectionReady)
            }
            denyDisabled={submitState !== null || !canSubmit}
            onApprove={() => submitConsent(true)}
            onDeny={() => submitConsent(false)}
          />
        </ConsentFooter>
      }
    />
  );
}
